/** API_SPEC.md §2 — auth. Session is a cookie; the browser never sees a token. */
import { api } from './client';
import type { AppHandoffResponse, AuthProviders, MagicLinkRequest, OAuthStartOptions, SessionResponse } from './types';

export const authApi = {
  /** Always 200 `{sent:true}` (no account enumeration). 400 on a malformed address, 429 past 5/h. */
  requestMagicLink: (body: MagicLinkRequest) => api<{ sent: true }>('/v1/auth/magic-link', { method: 'POST', body }),
  /** 200 `{user}` or 401. `silent401` because a missing session is the normal answer here. */
  session: () => api<SessionResponse>('/v1/auth/session', { silent401: true }),
  signOut: () => api<void>('/v1/auth/sign-out', { method: 'POST' }),
  /** Which sign-in providers the server can complete (OAuth ticket 01). Public: the sign-in screen asks before anyone is signed in. */
  providers: () => api<AuthProviders>('/v1/auth/providers'),
  /**
   * OAuth is a full-page redirect the backend owns (GET …/start → provider → …/callback). Everything the
   * sign-in must carry rides this one request, never the provider's URL: `termsVersion` is the agreement box
   * (closes contract gap 29, so a ticked box is recorded for an OAuth sign-in as for a link), `accessCode` the
   * pre-launch invite code, `redirectTo` the deep link. Each is left out when empty; the server alone decides
   * whether a code was needed and whether it is right.
   */
  /**
   * Android app ticket 08: a 2-minute single-use login token for the app, minted for the signed-in session. The token
   * is a bearer credential: it goes into the `intent:` link the person taps and nowhere else (no storage, no URL of
   * the page, no analytics, no console). CONTRACT GAP 55.
   */
  appHandoff: (redirectTo?: string | null) =>
    api<AppHandoffResponse>('/v1/auth/app-handoff', { method: 'POST', body: { redirectTo: redirectTo ?? null } }),
  oauthStartUrl: (provider: 'google' | 'apple', opts: OAuthStartOptions = {}) => {
    const query = new URLSearchParams();
    if (opts.redirectTo) query.set('redirectTo', opts.redirectTo);
    if (opts.termsVersion) query.set('termsVersion', opts.termsVersion);
    if (opts.accessCode) query.set('accessCode', opts.accessCode);
    const qs = query.toString();
    return `/v1/auth/oauth/${provider}/start` + (qs ? `?${qs}` : '');
  },
};
